// V-P1 · Company & Settings — consolidated landing page.
//
// PO-approved single location that gathers every existing settings
// surface behind one card grid: Company Profile · Document Defaults ·
// Closeout Defaults · Pricing Settings · Levi Settings (voice /
// persona / language) · Account / Subscription · Contractor Website
// Starter promo.
//
// STRICT SCOPE. This page does NOT introduce new business logic, new
// routes for existing surfaces, new schemas, or hidden feature flags.
// It only visually consolidates existing destinations so the sidebar
// can be simplified without breaking any existing deep link.
//
// Website Starter artwork is a labelled placeholder — the final
// promotional mockup will be provided/approved by the Product Owner +
// Chat Engineer separately.
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLang } from "@/context/LangContext";
import {
  Building2,
  FileText,
  ClipboardCheck,
  Settings as SettingsIcon,
  Sparkles,
  CreditCard,
  Globe,
  ArrowUpRight,
  Users,
  MessageSquare,
} from "lucide-react";

const SettingCard = ({ icon: Icon, overline, title, body, to, testid, accent }) => (
  <Link
    to={to}
    data-testid={testid}
    className={
      "jc-card p-5 flex flex-col gap-2 hover:shadow-md transition group " +
      (accent ? "border-l-4 border-[var(--jc-blue)]" : "")
    }
  >
    <div className="flex items-center gap-3">
      <div className="shrink-0 w-10 h-10 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="overline text-[10px]">{overline}</div>
        <div className="font-semibold text-sm truncate">{title}</div>
      </div>
      <ArrowUpRight
        size={14}
        className="text-[var(--jc-text-soft)] group-hover:text-[var(--jc-blue)] shrink-0"
      />
    </div>
    <p className="text-xs text-[var(--jc-text-soft)]">{body}</p>
  </Link>
);

export default function CompanySettings() {
  const { t } = useLang();
  return (
    <DashboardLayout
      title={t("company_settings.title")}
      subtitle={t("company_settings.subtitle")}
    >
      <div data-testid="company-settings-page" className="space-y-5 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SettingCard
            icon={Building2}
            overline={t("company_settings.company_overline")}
            title={t("company_settings.company_title")}
            body={t("company_settings.company_body")}
            to="/app/company-profile"
            testid="cs-card-company-profile"
          />
          <SettingCard
            icon={FileText}
            overline={t("company_settings.documents_overline")}
            title={t("company_settings.documents_title")}
            body={t("company_settings.documents_body")}
            to="/app/setup/document-defaults"
            testid="cs-card-document-defaults"
          />
          <SettingCard
            icon={ClipboardCheck}
            overline={t("company_settings.closeout_overline")}
            title={t("company_settings.closeout_title")}
            body={t("company_settings.closeout_body")}
            to="/app/setup/closeout-defaults"
            testid="cs-card-closeout-defaults"
          />
          <SettingCard
            icon={SettingsIcon}
            overline={t("company_settings.pricing_overline")}
            title={t("company_settings.pricing_title")}
            body={t("company_settings.pricing_body")}
            to="/app/pricing-profile"
            testid="cs-card-pricing-settings"
            accent
          />
          <SettingCard
            icon={Sparkles}
            overline={t("company_settings.levi_overline")}
            title={t("company_settings.levi_title")}
            body={t("company_settings.levi_body")}
            to="/app/settings/levi"
            testid="cs-card-levi-settings"
          />
          <SettingCard
            icon={CreditCard}
            overline={t("company_settings.account_overline")}
            title={t("company_settings.account_title")}
            body={t("company_settings.account_body")}
            to="/app/account"
            testid="cs-card-account"
          />
          <SettingCard
            icon={Users}
            overline="Team"
            title="Team Members"
            body="Invite people to your company, revoke pending invites, and remove members who no longer work here."
            to="/app/team"
            testid="cs-card-team-members"
          />
          <SettingCard
            icon={MessageSquare}
            overline="Communications"
            title="Business Texting"
            body="Set up your dedicated Twilio number for customer SMS. Required before your first live text."
            to="/app/settings/business-texting"
            testid="cs-card-business-texting"
          />
        </div>

        {/* Contractor Website Starter — promo placeholder. Final
            promotional artwork will be supplied by PO + Chat Engineer. */}
        <div
          className="jc-card overflow-hidden border-l-4 border-[var(--jc-blue)]"
          data-testid="cs-website-starter"
        >
          <div className="grid md:grid-cols-2">
            <div className="p-6">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-[var(--jc-blue)]" />
                <div className="overline">{t("company_settings.website_overline")}</div>
              </div>
              <div className="font-display text-2xl mt-1">
                {t("company_settings.website_headline")}
              </div>
              <div className="font-display text-xl mt-2 text-[var(--jc-blue)]">
                {t("company_settings.website_product")}
              </div>
              <p className="text-sm text-[var(--jc-text-soft)] mt-3">
                {t("company_settings.website_body")}
              </p>
              <ul className="mt-3 text-xs text-[var(--jc-text-soft)] space-y-1">
                <li>· {t("company_settings.website_bullet_customized")}</li>
                <li>· {t("company_settings.website_bullet_hosting")}</li>
                <li>· {t("company_settings.website_bullet_domain")}</li>
              </ul>
              <button
                type="button"
                data-testid="cs-website-starter-learn-more"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold jc-btn-blue"
              >
                {t("company_settings.website_cta")}
              </button>
            </div>
            <div className="relative min-h-[220px] bg-gradient-to-br from-[var(--jc-blue)]/10 via-[var(--jc-blue)]/5 to-transparent flex items-center justify-center">
              <div
                data-testid="cs-website-starter-placeholder"
                className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-dashed border-amber-500 bg-amber-50 text-amber-800"
              >
                {t("company_settings.website_placeholder_label")}
              </div>
              <Globe size={72} className="text-[var(--jc-blue)]/40" strokeWidth={1.2} />
            </div>
          </div>
        </div>

        <p className="text-xs text-[var(--jc-text-soft)] italic text-center pb-6">
          {t("company_settings.footer_tagline")}
        </p>
      </div>
    </DashboardLayout>
  );
}
